(() => {
  if (window.__kenzioSampleCheckoutFixV1) return;
  window.__kenzioSampleCheckoutFixV1 = true;

  const SAMPLE_HANDLE = 'kenzio-paid-sample';
  const READY_MOQ = 10;
  let refreshTimer = null;
  let cartRequest = null;

  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    return `₹${value.toLocaleString('en-IN', {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const isSampleItem = (item) =>
    item?.properties?._kenzio_sample === 'true' || item?.handle === SAMPLE_HANDLE;

  const readyItems = (cart) => (cart?.items || []).filter((item) => !isSampleItem(item));

  function cartMode(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length) return 'empty';

    const sampleCount = items.filter(isSampleItem).length;
    if (!sampleCount) return 'ready-only';

    const ready = readyItems(cart);
    if (!ready.length) return 'sample-only';

    const readyValid = ready.every((item) => Number(item.quantity || 0) >= READY_MOQ);
    return readyValid ? 'mixed-valid' : 'mixed-invalid';
  }

  const canDirectCheckout = (mode) => mode === 'sample-only' || mode === 'mixed-valid';

  async function getCart() {
    if (cartRequest) return cartRequest;
    cartRequest = fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to read cart');
        return response.json();
      })
      .finally(() => { cartRequest = null; });
    return cartRequest;
  }

  function checkoutPropertiesForItem(item) {
    const properties = { ...(item?.properties || {}) };
    const productTitle =
      properties._original_product_title ||
      properties.PRODUCT ||
      properties['Sample Of'] ||
      'Kenzio Product Sample';
    const selected =
      properties._original_variant_title ||
      properties.SELECTED ||
      properties.Selected ||
      '';

    delete properties['Order Type'];
    delete properties['Sample Of'];
    delete properties.Selected;

    properties.PRODUCT = productTitle;
    properties.ORDER = '1 PC PAID SAMPLE';
    properties['SAMPLE RATE'] = money(item?.final_price ?? item?.price ?? 0);

    if (selected && selected !== 'Default Title') properties.SELECTED = selected;
    else delete properties.SELECTED;

    return properties;
  }

  function metadataNeedsUpdate(item) {
    if (!isSampleItem(item)) return false;
    const properties = item?.properties || {};
    const productTitle =
      properties._original_product_title ||
      properties.PRODUCT ||
      properties['Sample Of'] ||
      '';
    const selected =
      properties._original_variant_title ||
      properties.SELECTED ||
      properties.Selected ||
      '';
    const expectedRate = money(item?.final_price ?? item?.price ?? 0);

    if (!properties.PRODUCT || properties.PRODUCT !== productTitle) return true;
    if (properties.ORDER !== '1 PC PAID SAMPLE') return true;
    if (properties['SAMPLE RATE'] !== expectedRate) return true;
    if (selected && selected !== 'Default Title' && properties.SELECTED !== selected) return true;
    if (properties['Order Type'] || properties['Sample Of'] || properties.Selected) return true;
    return false;
  }

  async function ensureCheckoutMetadata(cart) {
    const targets = (cart?.items || []).filter(metadataNeedsUpdate);
    if (!targets.length) return cart;

    for (const item of targets) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: Number(item.quantity || 1),
          properties: checkoutPropertiesForItem(item),
        }),
      });
      if (!response.ok) throw new Error('Unable to prepare sample details for checkout');
    }

    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((response) => {
      if (!response.ok) throw new Error('Unable to verify updated cart');
      return response.json();
    });
  }

  function sampleRows(cart) {
    const rows = [];
    (cart?.items || []).forEach((item, index) => {
      if (!isSampleItem(item)) return;
      const line = index + 1;
      const pageRow = document.getElementById(`CartItem-${line}`);
      const drawerRow = document.getElementById(`CartDrawer-Item-${line}`);
      if (pageRow) rows.push(pageRow);
      if (drawerRow) rows.push(drawerRow);
    });
    return rows;
  }

  function markSampleMoqExempt(cart) {
    sampleRows(cart).forEach((row) => {
      row.classList.add('kz-sample-line');
      const input = row.querySelector('input[name="updates[]"][type="number"], input[name="updates[]"]');
      if (!input) return;
      input.min = '1';
      input.step = '1';
      input.dataset.min = '1';
      input.dataset.kenzioMoq = '1';
      if (Number(input.value || 1) === 1) input.setAttribute('value', '1');
    });
  }

  function hideYukoWarningsOnSampleRows(cart) {
    const needle = 'you must select at least 10 products';

    sampleRows(cart).forEach((row) => {
      const matches = Array.from(row.querySelectorAll('div, p, span')).filter((element) => {
        const text = (element.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        return text.includes(needle);
      });

      matches.forEach((element) => {
        const hasMatchingChild = Array.from(element.children || []).some((child) => {
          const childText = (child.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
          return childText.includes(needle);
        });
        if (hasMatchingChild) return;

        const baseText = (element.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        let target = element;
        while (target.parentElement && target.parentElement !== row) {
          const parent = target.parentElement;
          const parentText = (parent.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
          if (parentText !== baseText) break;
          target = parent;
        }

        target.style.setProperty('display', 'none', 'important');
        target.setAttribute('aria-hidden', 'true');
      });
    });
  }

  function restoreStandardCheckout(button) {
    if (button.dataset.kzSampleCheckoutHidden !== '1') return;
    button.style.removeProperty('display');
    button.removeAttribute('aria-hidden');
    delete button.dataset.kzSampleCheckoutHidden;
  }

  function checkoutUi(mode) {
    if (mode === 'mixed-valid') {
      return {
        eyebrow: 'READY + SAMPLE CHECKOUT',
        note: 'Ready products meet MOQ • paid samples are MOQ-exempt',
        button: 'CHECKOUT READY + SAMPLES',
      };
    }
    return {
      eyebrow: 'PAID SAMPLE CHECKOUT',
      note: '1 pc samples • MOQ does not apply',
      button: 'CHECKOUT SAMPLE ORDER',
    };
  }

  function renderDirectCheckoutButton(direct, mode) {
    const ui = checkoutUi(mode);
    direct.dataset.kzCheckoutMode = mode;
    direct.innerHTML = `<span>${ui.button}</span><span>→</span>`;
  }

  function makeDirectCheckoutButton(standardButton, mode) {
    const parent = standardButton.parentElement;
    if (!parent) return;

    standardButton.dataset.kzSampleCheckoutHidden = '1';
    standardButton.style.setProperty('display', 'none', 'important');
    standardButton.setAttribute('aria-hidden', 'true');

    const ui = checkoutUi(mode);

    let note = parent.querySelector('.kz-sample-checkout-note');
    if (!note) {
      note = document.createElement('div');
      note.className = 'kz-sample-checkout-note';
      parent.insertBefore(note, standardButton);
    }
    note.setAttribute('data-kz-sample-checkout-note', '');
    note.innerHTML = `<strong>${ui.eyebrow}</strong><span>${ui.note}</span>`;

    let direct = parent.querySelector('[data-kz-sample-direct-checkout]');
    if (!direct) {
      direct = document.createElement('button');
      direct.type = 'button';
      direct.className = 'button kz-sample-direct-checkout';
      direct.setAttribute('data-kz-sample-direct-checkout', '');
      parent.insertBefore(direct, standardButton);

      direct.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        direct.disabled = true;
        direct.classList.add('is-loading');
        direct.innerHTML = '<span>PREPARING ORDER…</span><span>•</span>';

        try {
          let freshCart = await fetch('/cart.js', {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          }).then((response) => {
            if (!response.ok) throw new Error('Unable to verify cart');
            return response.json();
          });

          let modeNow = cartMode(freshCart);
          if (!canDirectCheckout(modeNow)) {
            window.location.reload();
            return;
          }

          freshCart = await ensureCheckoutMetadata(freshCart);
          modeNow = cartMode(freshCart);

          if (!canDirectCheckout(modeNow)) {
            window.location.reload();
            return;
          }

          direct.innerHTML = '<span>OPENING CHECKOUT…</span><span>•</span>';
          window.location.assign('/checkout');
        } catch (error) {
          console.error('[Kenzio Samples] Sample checkout failed', error);
          direct.disabled = false;
          direct.classList.remove('is-loading');
          renderDirectCheckoutButton(direct, direct.dataset.kzCheckoutMode || 'sample-only');
        }
      }, true);
    }

    note.style.removeProperty('display');
    note.removeAttribute('aria-hidden');
    direct.style.removeProperty('display');
    direct.removeAttribute('aria-hidden');
    direct.disabled = false;
    direct.classList.remove('is-loading');
    renderDirectCheckoutButton(direct, mode);
  }

  function removeSampleCheckoutUi(parent) {
    parent?.querySelectorAll('[data-kz-sample-direct-checkout], .kz-sample-checkout-note').forEach((node) => node.remove());
  }

  function applyCheckoutMode(cart) {
    const mode = cartMode(cart);
    const hasSamples = mode === 'sample-only' || mode === 'mixed-valid' || mode === 'mixed-invalid';

    document.documentElement.classList.toggle('kz-sample-only-cart', mode === 'sample-only');
    document.documentElement.classList.toggle('kz-mixed-sample-cart', mode === 'mixed-valid' || mode === 'mixed-invalid');

    if (hasSamples) {
      markSampleMoqExempt(cart);
      hideYukoWarningsOnSampleRows(cart);
    }

    const buttons = new Set([
      ...document.querySelectorAll('#checkout'),
      ...document.querySelectorAll('#CartDrawer-Checkout'),
      ...document.querySelectorAll('button[name="checkout"]'),
      ...document.querySelectorAll('input[name="checkout"]'),
    ]);

    buttons.forEach((button) => {
      if (button.hasAttribute('data-kz-sample-direct-checkout')) return;

      if (canDirectCheckout(mode)) {
        makeDirectCheckoutButton(button, mode);
      } else {
        restoreStandardCheckout(button);
        removeSampleCheckoutUi(button.parentElement);
      }
    });
  }

  async function refresh() {
    try {
      const cart = await getCart();
      applyCheckoutMode(cart);
    } catch (error) {
      console.error('[Kenzio Samples] Checkout mode error', error);
    }
  }

  function queueRefresh() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, 90);
  }

  const start = () => {
    refresh();
    const observer = new MutationObserver(queueRefresh);
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(refresh, 300);
    window.setTimeout(refresh, 900);
    window.setTimeout(refresh, 1800);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();